<template>
  <div class="productPage">
    <div class="productPage__inner container">
      <div class="productPage__stickyBreadcrumb">
        <mobile-breadcrumbs
          :title="getCurrentCategory.title"
          :to="localePath({ name: 'category-slug', params: { slug: getCurrentCategory.slug } })"
        />
      </div>
      <div v-if="string" class="productPage__mobileBreadcrumb">
        <mobile-breadcrumbs
          :title="`Wróć do wyszukiwania „${string}”`"
          :to="
            localePath({
              name: 'search',
              query: {
                query: string,
              },
            })
          "
          small
        />
      </div>
      <breadcrumbs
        :list="productData.breadcrumbs"
        class="productPage__breadcrumbs"
        last-clickable
        search-categories
      />
      <div data-sticky-container class="productPage__content">
        <div class="productPage__contentTop">
          <div class="productPage__header">
            <product-header
              :product-data="productData.product"
              :is-on-shopping-list="isOnShoppingList"
            />
          </div>
          <div class="productPage__sidebar">
            <product-sidebar
              :main-product="productData.outletMainItem"
              :product="productData.product"
              :outlet="productData.outletProducts"
              :is-on-shopping-list="isOnShoppingList"
            />
          </div>
        </div>
        <div class="productPage__nav">
          <div class="productPage__navInner">
            <div class="productPage__navTop" @click="scrollProductToTop"></div>
            <ul v-scroll-spy-active v-scroll-spy-link class="productPage__navList">
              <li class="productPage__navItem">
                <a class="productPage__navLink">Opis produktu</a>
              </li>
              <li v-if="productData.product.attributesForFrontend" class="productPage__navItem">
                <a class="productPage__navLink">Specyfikacja</a>
              </li>
              <li
                v-if="
                  productData.product.compatibleDevices &&
                  productData.product.compatibleDevices.length
                "
                class="productPage__navItem"
              >
                <a class="productPage__navLink">Kompatybilne urządzenia </a>
              </li>
              <li v-if="productData.product.nutritionalValues" class="productPage__navItem">
                <a class="productPage__navLink">Wartości odżywcze</a>
              </li>
              <li v-if="productData.product.ingredients" class="productPage__navItem">
                <a class="productPage__navLink">Składniki</a>
              </li>
              <li v-if="productData.product.fileAttachments" class="productPage__navItem">
                <a class="productPage__navLink">Pliki do pobrania</a>
              </li>
              <li v-if="similarProducts" class="productPage__navItem">
                <a class="productPage__navLink">Podobne</a>
              </li>
              <li v-if="accessories" class="productPage__navItem">
                <a class="productPage__navLink">Akcesoria</a>
              </li>
            </ul>
          </div>
        </div>

        <div
          v-scroll-spy="{ offset: 146 }"
          class="productPage__sections productPage__outletSection productPage__outletSection--mobile"
          v-if="productData.product.outletType"
        >
          <LazyHydrate when-visible>
            <product-section class="productSection--outlet" mobile-full>
              <div class="productPage__outletContainer">
                <span class="productPage__outletText">Wariant OUTLET – Niższa cena!</span>
                <span v-html="productData.product.outletType.description"></span>
              </div>
            </product-section>
          </LazyHydrate>
        </div>
        <div
          v-scroll-spy="{ offset: 146 }"
          class="productPage__sections productPage__outletSection"
          v-if="productData.outletProducts && productData.outletProducts.length > 0"
        >
          <LazyHydrate when-visible>
            <product-section
              v-for="item in productData.outletProducts"
              :key="item.id"
              class="productSection--outlet"
              title="Wariant OUTLET – Niższa cena!"
              product-name="Skorzystaj z okazji i kup taniej."
              mobile-full
            >
              <div class="productPage__outletContainer">
                <div class="productPage__outletText" v-html="item.outletType.description"></div>
                <NuxtLink
                  class="productPage__outletButton productSidebar__outletButton"
                  :to="
                    localePath({
                      name: 'product-slug',
                      params: { slug: item.slug },
                    })
                  "
                >
                  <div class="productSidebar__outletButtonTitle">Outlet</div>
                  <div class="productSidebar__outletButtonPrice">
                    <span>
                      {{
                        showNetPrices
                          ? item.priceObject.netFormatted
                          : item.priceObject.grossFormatted
                      }}
                    </span>
                    {{ item.priceObject.currencyPostfix }}
                    {{ showNetPrices ? $t('net') : $t('gross') }}
                    <span class="productSidebar__buttonArrow icon-chevron-right"></span>
                  </div>
                </NuxtLink>
              </div>
            </product-section>
          </LazyHydrate>
        </div>
        <div v-scroll-spy="{ offset: 146 }" class="productPage__sections">
          <product-section
            v-if="gratisPromotions && gratisPromotions.length"
            mobile-full
            title="Promocje"
          >
            <template v-for="(promotion, index) in gratisPromotions">
              <checkout-product-extras
                v-if="promotion && promotion.descriptionForItemHtml"
                :key="`promotion_${index}`"
                :content="promotion.descriptionForItemHtml"
              />
            </template>
          </product-section>
          <LazyHydrate never>
            <product-section
              mobile-full
              :title="`Elementy zestawu (${productData.product.members.length})`"
              :h-tag="false"
              :product-name="productData.product.name"
              v-if="productData.product.members && productData.product.members.length > 0"
            >
              <product-pack-members-big
                :members="productData.product.members"
                :product="productData.product"
              />
            </product-section>
          </LazyHydrate>

          <LazyHydrate when-visible>
            <product-section
              :product-name="productData.product.name"
              mobile-full
              title="Opis produktu"
            >
              <div class="productPage__descItem">
                <cms-wysiwyg
                  :html="productData.product.descriptionShort"
                  v-if="productData.product.descriptionShort"
                />
              </div>
              <cms-wysiwyg
                :html="productData.product.descriptionLong"
                v-if="productData.product.descriptionLong"
              />
              <product-desc-list
                :list="productData.product.description.blocks"
                v-if="productData.product.description"
              />
            </product-section>
          </LazyHydrate>

          <LazyHydrate when-visible>
            <product-section
              class="productPage__specification"
              title="Specyfikacja"
              :product-name="productData.product.name"
              v-if="
                (productData.product.attributesForFrontend &&
                  productData.product.attributesForFrontend.length) ||
                (productData.product.responsibleProducers &&
                  productData.product.responsibleProducers.length)
              "
            >
              <product-specification
                :data="productData.product.attributesForFrontend"
                :producent="productData.product.responsibleProducers"
                :safety-informations="productData.product.safetyInformations"
                class="productPage__specification"
              />
            </product-section>
          </LazyHydrate>

          <LazyHydrate never>
            <product-section
              mobile-full
              title="Kompatybilne urządzenia"
              :h-tag="false"
              v-if="
                productData.product.compatibleDevices &&
                productData.product.compatibleDevices.length
              "
            >
              <cms-wysiwyg :html="compatibleDevices" />
            </product-section>
          </LazyHydrate>

          <LazyHydrate never>
            <product-section
              mobile-full
              title="Wartości odżywcze"
              :h-tag="false"
              :product-name="productData.product.name"
              v-if="productData.product.nutritionalValues"
            >
              <cms-wysiwyg :html="productData.product.nutritionalValues" />
            </product-section>
          </LazyHydrate>

          <LazyHydrate never>
            <product-section
              mobile-full
              :h-tag="false"
              title="Składniki"
              v-if="productData.product.ingredients"
            >
              <cms-wysiwyg :html="productData.product.ingredients" />
            </product-section>
          </LazyHydrate>

          <LazyHydrate never>
            <product-section
              mobile-full
              :h-tag="false"
              title="Pliki do pobrania"
              v-if="productData.product.fileAttachments"
            >
              <product-files :files="productData.product.fileAttachments" />
            </product-section>
          </LazyHydrate>

          <product-similar
            :products="productSimilar"
            @productSectionData="similarProducts = true"
          />

          <product-accessories
            :products="productAccessories"
            @productSectionData="accessories = true"
          />
        </div>
      </div>
    </div>
    <div class="productPage__bottom">
      <LazyHydrate when-visible>
        <product-others :products="productOthers" />
      </LazyHydrate>
    </div>

    <flexible-slider-section
      :section="{ title: 'Zobacz w zestawie', data: { products: productData.product.packs } }"
      :h-tag="false"
      v-if="productData.product.packs && productData.product.packs.length"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex';
import LazyHydrate from 'vue-lazy-hydration';
import Sticky from 'sticky-js';
import MobileBreadcrumbs from '~/components/MobileBreadcrumbs';
import Breadcrumbs from '~/components/Breadcrumbs';
import ProductSidebar from '~/components/Product/ProductSidebar';
import ProductHeader from '~/components/Product/ProductHeader';
import CheckoutProductExtras from '~/components/Checkout/CheckoutProductExtras';
import listMixin from '~/mixins/list';
import ProductPackMembersBig from '@/components/Product/ProductPackMembersBig.vue';
import FlexibleSliderSection from '@/components/Flexible/FlexibleSliderSection.vue';

export default {
  name: 'ProductPage',
  components: {
    FlexibleSliderSection,
    ProductPackMembersBig,
    LazyHydrate,
    MobileBreadcrumbs,
    Breadcrumbs,
    ProductSidebar,
    ProductHeader,
    CheckoutProductExtras,
    ProductOthers: () => import('~/containers/Product/ProductOthers'),
    ProductAccessories: () => import('~/containers/Product/ProductAccessories'),
    ProductSimilar: () => import('~/containers/Product/ProductSimilar'),
    CmsWysiwyg: () => import('~/components/Cms/CmsWysiwyg'),
    ProductDescList: () => import('~/components/Product/ProductDescList'),
    ProductSection: () => import('~/components/Product/ProductSection'),
    ProductFiles: () => import('~/components/Product/ProductFiles'),
    ProductSpecification: () => import('~/components/Product/ProductSpecification'),
  },
  mixins: [listMixin],
  async asyncData({ app, $config, error, $axios, route, store, redirect, $cookies }) {
    try {
      const result = await Promise.all([
        $axios.get(`${$config.baseOfixApi}/e-commerce/v1/products/${route.params.slug}`, {
          params: {
            includeBreadcrumbs: true,
            includeProduct: true,
            includeSeoData: true,
            includeAccessories: false,
            includeSimilarProducts: false,
            includeOtherProducts: false,
            includeOtherProductsWithSameBrand: false,
          },
          useCache: true,
        }),
        $axios
          .get(
            `${$config.baseOfixApi}/e-commerce/v1/products/${route.params.slug}/gratisPromotions`,
            {
              useCache: true,
            },
          )
          .then(response => {
            return response;
          })
          .catch(() => {
            return [];
          }),
        ...(process.server
          ? [
              $axios.get(`${$config.baseOfixApi}/e-commerce/v1/products/${route.params.slug}`, {
                params: {
                  includeBreadcrumbs: false,
                  includeProduct: false,
                  includeSeoData: false,
                  includeAccessories: true,
                  includeSimilarProducts: false,
                  includeOtherProducts: false,
                  includeOtherProductsWithSameBrand: false,
                },
                useCache: true,
              }),
              $axios
                .get(`${$config.baseOfixApi}/e-commerce/v1/products/${route.params.slug}`, {
                  params: {
                    includeBreadcrumbs: false,
                    includeProduct: false,
                    includeSeoData: false,
                    includeAccessories: false,
                    includeSimilarProducts: true,
                    includeOtherProducts: false,
                    includeOtherProductsWithSameBrand: false,
                  },
                  useCache: true,
                })
                .then(response => {
                  return response;
                })
                .catch(() => {
                  return [];
                }),
              $axios
                .get(`${$config.baseOfixApi}/e-commerce/v1/products/${route.params.slug}`, {
                  params: {
                    includeBreadcrumbs: false,
                    includeProduct: false,
                    includeSeoData: false,
                    includeAccessories: false,
                    includeSimilarProducts: false,
                    includeOtherProducts: false,
                    includeOtherProductsWithSameBrand: true,
                  },
                  useCache: true,
                })
                .then(response => {
                  return response;
                })
                .catch(() => {
                  return [];
                }),
            ]
          : []),
      ]);

      store.commit('layout/setHeaderPrefix', 'searchPage');
      store.commit('layout/scrollDownMobileHide', true);
      store.commit('layout/initialMobileHideSearch', true);

      if (result[0].data.product.slug !== route.params.slug) {
        redirect(
          301,
          app.localePath({ name: 'product-slug', params: { slug: result[0].data.product.slug } }),
        );
      }

      return {
        productData: result[0].data,
        gratisPromotions: result[1] && result[1].data ? result[1].data.promotions : [],
        productSimilar: result[2] && result[2].data ? result[2].data.similarProducts : [],
        productAccessories: result[3] && result[3].data ? result[3].data.accessories : [],
        productOthers: result[4] && result[4].data ? result[4].data.otherProductsWithSameBrand : [],
      };
    } catch (e) {
      if (e.response && e.response.status === 410) {
        const category = e.response.data.categories[0];
        if (category) {
          $cookies.set('previous-url', route.fullPath, {
            path: '/',
            maxAge: 60 * 60 * 24 * 7,
          });
          redirect(301, app.localePath({ name: 'category-slug', params: { slug: category.slug } }));
        } else {
          error({
            statusCode: 200,
            isProduct: true,
            breadcrumbs: e.response.data.breadcrumbs,
            products: e.response.data.similarProducts,
            categories: e.response.data.categories,
          });
        }
      } else {
        error({
          statusCode: e?.response?.status || 404,
          message: e?.response?.data?.message || 'Product not found',
        });
      }
    }
  },
  nuxtI18n: {
    paths: {
      pl: '/produkt/:slug',
      en: '/product/:slug',
    },
  },
  data() {
    return {
      isOnShoppingList: false,
      productData: null,
      accessories: false,
      similarProducts: false,
      otherProducts: false,
      stickyNav: null,
    };
  },
  head() {
    const tags = [
      {
        name: 'robots',
        content: 'index, follow',
      },
    ];

    const links = [];
    const gallery = this.productData?.product?.gallery;

    if (Array.isArray(gallery?.stat) && gallery.stat.length > 0) {
      gallery.stat.forEach(image => {
        links.push({
          hid: 'product-small-image',
          rel: 'preload',
          href: this.$helper.optimizeImageUrl(image.urlFull, 'w_80,h_60'),
          as: 'image',
          type: 'image/webp',
        });
      });
    }

    if (gallery?.main?.urlFull) {
      links.push({
        hid: 'product-main-image',
        rel: 'preload',
        href: this.$helper.optimizeImageUrl(
          gallery.main.urlFull,
          this.$device.isMobileOrTablet ? 'w_400,h_250' : 'w_500,h_380',
        ),
        as: 'image',
        type: 'image/webp',
      });
    }

    return {
      title: this.productData?.seo?.title ?? '',
      meta: this.$helper.metaDescription([...tags, ...(this.productData?.seo?.metaTags ?? [])]),
      script: [...this.$helper.schemaData(this.productData?.seo?.schemaOrgData ?? [])],
      link: links,
    };
  },
  computed: {
    ...mapState('autocomplete', ['string']),
    ...mapGetters('auth', ['showNetPrices']),
    compatibleDevices() {
      let compatibleDevices = '';
      this.productData.product.compatibleDevices.forEach((el, i, array) => {
        compatibleDevices =
          compatibleDevices + `${el.brand} ${el.model}${i !== array.length - 1 ? ', ' : ''}`;
      });

      return `<p>${compatibleDevices}</p>`;
    },
    getCurrentCategory() {
      return this.productData.breadcrumbs[this.productData.breadcrumbs.length - 1];
    },
  },
  created() {
    this.setHeaderPrefix('searchPage');
    this.scrollDownMobileHide(true);
    this.initialMobileHideSearch(true);
    this.isOnShoppingList = this.checkIfInList(this.productData.product.id);
    this.unwatchShoppingList = this.$store.watch(
      () => (this.$auth.isLogged() ? this.shoppingLists : this.defaultShoppingList),
      newValue => {
        this.isOnShoppingList = this.checkIfInList(this.productData.product.id);
      },
    );
  },
  beforeDestroy() {
    this.unwatchShoppingList();
  },
  destroyed() {
    this.setHeaderPrefix('');
    this.scrollDownMobileHide(false);
    this.initialMobileHideSearch(false);
    window.removeEventListener('resize', this.stickySectionNav);
  },
  mounted() {
    if (this.$nuxt.$loading._isVue) {
      if (this.$nuxt.$loading.finishCreate()) {
        this.$nuxt.$loading.finishCreate();
      }
    }

    const item = this.productData.product;

    const categories = item.category.namesInPath.reduce((acc, curr, index) => {
      acc['category' + (index === 0 ? '' : index + 1)] = curr;
      return acc;
    }, {});

    this.$gtm.push({ ecommerce: null });
    this.$gtm.push({
      event: 'view_item',
      ecommerce: {
        item_list_id: '',
        item_list_name: '',
        items: [
          {
            item_id: item.id,
            item_name: item.name,
            discount: item.priceBeforeDiscountObject
              ? this.$helper.calculateDiscount(
                  item.priceBeforeDiscountObject.gross,
                  item.priceObject.gross,
                )
              : 0,
            brand: item.brand ? item.brand.name : '',
            ...categories,
            item_list_id: '',
            item_list_name: '',
            price: item.priceObject.gross,
            variant: '',
            quantity: 1,
            promotion_id: item?.promotion?.id ?? '',
            promotion_name: item?.promotion?.name ?? '',
          },
        ],
      },
    });

    this.stickySidebar = new Sticky('.productPage__sidebar', {
      stickyClass: 'productPage__sidebar--sticky',
      marginTop: 160,
      stickyFor: 1279,
    });

    this.stickySidebar = new Sticky('.productPage__stickyBreadcrumb', {
      stickyClass: 'mobileBreadcrumbs--sticky',
      marginTop: 0,
      wrap: true,
    });

    this.stickySectionNav();
    window.addEventListener('resize', this.stickySectionNav);

    const ageInformationPopupCookie = this.$cookies.get('ageInformationPopup');
    if (this.productData?.product?.category?.forAdults && !ageInformationPopupCookie) {
      this.setAgeInformationPopupShow(true);
    }
  },
  methods: {
    ...mapMutations({
      setHeaderPrefix: 'layout/setHeaderPrefix',
      scrollDownMobileHide: 'layout/scrollDownMobileHide',
      initialMobileHideSearch: 'layout/initialMobileHideSearch',
      setAgeInformationPopupShow: 'layout/setAgeInformationPopupShow',
    }),
    stickySectionNav() {
      let navOffset = 89;

      if (window.innerWidth < 1600) {
        navOffset = 80;
      }

      if (window.innerWidth < 768) {
        navOffset = 62;
      }

      this.stickyNav = new Sticky('.productPage__nav', {
        stickyClass: 'productPage__nav--sticky',
        wrap: true,
        marginTop: navOffset,
      });
    },
    scrollProductToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  },
};
</script>

<style lang="scss">
.productPage {
  $root: &;
  overflow: hidden;

  &__breadcrumbs {
    @include rwd('tablet') {
      display: none;
    }
  }

  &__stickyBreadcrumb,
  &__mobileBreadcrumb {
    display: none;

    @include rwd('tablet') {
      display: flex;
    }
  }

  &__contentTop {
    display: flex;
    flex-wrap: wrap;

    @include rwd('small-tablet') {
      width: calc(100% + 40px);
      margin-left: -20px;
    }
  }

  &__header {
    flex: 0 0 100%;
    max-width: 100%;
    padding-right: 0;

    @include rwd('laptop', true) {
      flex: 0 0 70%;
      max-width: 70%;
      padding-right: 8px;
    }

    @include rwd('large-laptop', true) {
      flex: 0 0 73%;
      max-width: 73%;
    }

    @include rwd('ultra', true) {
      flex: 0 0 75%;
      max-width: 75%;
    }
  }

  &__sidebar {
    pointer-events: none;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    z-index: 2;
    padding-left: 0;
    flex: 0 0 100%;
    max-width: 100%;
    box-shadow: 0 5px 15px rgba(23, 25, 51, 0.03);

    @include rwd('laptop', true) {
      flex: 0 0 30%;
      max-width: 30%;
      padding-left: 8px;
    }

    @include rwd('large-laptop', true) {
      flex: 0 0 27%;
      max-width: 27%;
      box-shadow: 0 5px 15px rgba(23, 25, 51, 0);
    }

    @include rwd('ultra', true) {
      flex: 0 0 25%;
      max-width: 25%;
    }

    @include rwd('small-tablet') {
      background-color: $cWhite;
      padding: 0 2rem 1rem;
    }
  }

  &__sections {
    flex: 0 0 100%;
    max-width: 100%;
    margin-top: 3rem;
    padding-right: 0;

    @include rwd('laptop', true) {
      padding-right: 8px;
      flex: 0 0 70%;
      max-width: 70%;
    }

    @include rwd('large-laptop', true) {
      flex: 0 0 73%;
      max-width: 73%;
    }

    @include rwd('ultra', true) {
      flex: 0 0 75%;
      max-width: 75%;
    }
  }

  &__nav {
    flex: 0 0 100%;
    max-width: 100%;
    position: relative;
    background-color: $cWhite;

    @include rwd('laptop', true) {
      flex: 0 0 calc(70% - 8px);
      max-width: calc(70% - 8px);
    }

    @include rwd('large-laptop', true) {
      flex: 0 0 calc(73% - 8px);
      max-width: calc(73% - 8px);
    }

    @include rwd('ultra', true) {
      flex: 0 0 calc(75% - 8px);
      max-width: calc(75% - 8px);
    }

    @include rwd('small-tablet') {
      flex: 0 0 calc(100% + 40px);
      max-width: calc(100% + 40px);
      margin-left: -20px;
      margin-right: -20px;
    }

    &:before {
      content: '';
      position: absolute;
      left: -50%;
      top: 0;
      width: 300%;
      height: 100%;
      background-color: $cWhite;
      opacity: 0;
      pointer-events: none;
      transition: opacity $tr;
      box-shadow: 0 3px 20px 0 rgba(23, 25, 51, 0.07);
    }

    &--sticky {
      z-index: $zHeader - 2;

      @include rwd('small-tablet') {
        padding-left: 20px;
        padding-right: 20px;
      }

      #{$root}__navInner {
        padding-left: 0;

        @include rwd('small-tablet') {
          padding-left: 20px;
          padding-right: 20px;
        }
      }

      &:before {
        opacity: 1;
      }
    }
  }

  &__navInner {
    display: flex;
    align-items: center;
    padding: 2rem;
    overflow: auto;
  }

  &__navTop {
    cursor: pointer;
    flex: 0 0 2.6rem;
    margin-left: 1rem;
    margin-right: 0.5rem;
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2.6rem;
    height: 2.6rem;
    border-radius: 50%;
    border: 1px solid $cAddLight;

    @include rwd('small-tablet') {
      margin-left: 0;
    }

    &:before {
      @include icomoon('chevron-up');
      color: $cMainLight;
      font-size: 6px;
    }
  }

  &__navList {
    display: flex;
    position: relative;
    z-index: 2;
  }

  &__navItem {
    margin-left: 2.5rem;
    margin-right: 2.5rem;
    font-size: 1.4rem;
    line-height: 1.8rem;
    font-weight: 600;
    color: $cAddDark02;
    transition: color $tr;
    white-space: nowrap;

    @include rwd('tablet') {
      margin-left: 1.5rem;
      margin-right: 1.5rem;
    }

    &.active,
    &:hover {
      color: $cMainDark;
    }
  }

  &__specification {
    @include rwd('small-tablet') {
      flex: 0 0 calc(100% + 40px);
      max-width: calc(100% + 40px);
      margin-left: -20px;
      margin-right: -20px;
      padding-bottom: 3.5rem;
    }
  }

  &__navLink {
    cursor: pointer;
  }

  &__descItem {
    margin-bottom: 1.5rem;
  }

  &__outletSection {
    margin-bottom: 1.5rem;
    @include rwd('large-phone') {
      .productSection__title {
        text-align: left;
      }
    }

    &--mobile {
      display: none;

      @include rwd('large-phone') {
        display: block;
        margin-bottom: -4rem;
      }
    }
  }

  &__outletContainer {
    padding: 1rem 1.6rem;
    background-color: rgba(255, 110, 0, 0.1);
    font-size: 1.4rem;
    line-height: 1.4;

    strong,
    b {
      font-weight: 700;
    }
  }

  &__outletButton {
    margin-top: 1rem;
    background: $cWhite;
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: -1px;
      left: -1px;
      height: calc(100% + 2px);
      border-left: 2px solid $cBadgeProm;
    }
  }

  &__outletText {
    font-weight: 700;
  }
}
</style>
